/**
 * Scientific chart tokens.
 *
 * - `chartTheme` — dark-native UI (soil-intelligence workspace).
 * - `printChartTheme` — high-contrast print / PDF capture (dark ink on paper).
 * - `getActiveChartTheme()` — switches when `documentElement[data-print-capture]`.
 */
import { flahaSoilColors } from "../../../../theme/flahaSoilTheme";

export const chartTheme = {
	// Axes, ticks, grid — higher contrast for dark workspace
	axis: "rgba(246, 240, 227, 0.55)",
	axisStrong: "rgba(246, 240, 227, 0.78)",
	grid: "rgba(246, 240, 227, 0.16)",
	// Labels: near-white cream (avoid soft brown wash-out)
	label: "#F6F0E3",
	labelMuted: "#D9CFBE",
	// Primary series — Flaha PA blue (bright on dark)
	seriesPrimary: "#5BB4D9",
	seriesPrimaryGlow: "rgba(91, 180, 217, 0.55)",
	// Agronomic anchors
	saturation: "#5BB4D9",
	fieldCapacity: "#8FCF6A",
	irrigation: "#E0A45A",
	wiltingPoint: "#F07167",
	// Base saturation cations — saturated, not pastel
	cationCa: "#8FCF6A",
	cationMg: "#5BB4D9",
	cationK: "#E0C36A",
	cationNa: "#F07167",
	cationOther: "rgba(217, 207, 190, 0.45)",
	// Severity / risk
	severityNone: "#8FCF6A",
	severitySlight: "#A8D97A",
	severityModerate: "#E0A45A",
	severityStrong: "#E07A2F",
	severitySevere: "#F07167",
	// Evidence ring
	ringTrack: "rgba(246, 240, 227, 0.18)",
	ringMet: "#8FCF6A",
	ringPartial: "#E0A45A",
	ringMissing: "#F07167",
	// Radar
	radarGrid: "rgba(246, 240, 227, 0.22)",
	radarFill: "rgba(91, 180, 217, 0.28)",
	radarStroke: "#5BB4D9",
	radarPointGood: "#8FCF6A",
	radarPointFair: "#E0A45A",
	radarPointPoor: "#F07167",
	// Plot frame — deeper charcoal for contrast
	plotBg: "rgba(8, 12, 10, 0.92)",
	plotBorder: "rgba(246, 240, 227, 0.22)",
	barTrack: "rgba(246, 240, 227, 0.14)",
	// Point stroke on dark
	pointStroke: "#0C100E",

	// ── Cation / structure triangle (exact asset geometry + themed fills) ──
	// Hierarchy mirrors the original diagram: ice upper → peach band → orange base;
	// good/optimal cores stay cool-blue (readable on dark workspace).
	// Transparent canvas — no white plate.
	structureBg: "transparent",
	structureOutline: "rgba(246, 240, 227, 0.45)",
	// Moderate family — light peach/sand band (not brown)
	structureModerate: "#E8C9A0",
	structureModerateSoft: "#F3DFC4",
	// Upper “reasonable” — pale ice blue (original #dfebf2 family)
	structureReasonable: "#C5D9E8",
	// Good ellipse — soft blue
	structureGood: "#8FB8D9",
	// Optimal core — stronger PA blue
	structureOptimal: "#3D8FB8",
	// Poor base — amber orange (original #ef7a0b family)
	structurePoor: "#E07A2F",
	// Sample marker (dark dot + cream ring — like original black point)
	structureSample: "#1A1E1C",
	structureSampleRing: "#F6F0E3",
	structureSampleHalo: "rgba(246, 240, 227, 0.35)",
	// Labels / ticks
	structureLabel: "#F6F0E3",
	structureLabelMuted: "rgba(246, 240, 227, 0.78)",
	structureTick: "rgba(246, 240, 227, 0.88)",
	structureArrow: "#E0A45A",
} as const;

/** Mutable chart colour map (UI + print share the same keys). */
export type ChartTheme = { readonly [K in keyof typeof chartTheme]: string };

/**
 * Print / PDF capture theme — dark ink, light plate, no soft cream labels.
 * Used while `document.documentElement.dataset.printCapture === "true"`.
 */
export const printChartTheme: ChartTheme = {
	...chartTheme,
	axis: "rgba(36, 26, 20, 0.55)",
	axisStrong: "rgba(36, 26, 20, 0.78)",
	grid: "rgba(36, 26, 20, 0.14)",
	label: "#241A14",
	labelMuted: "#5C4A38",
	seriesPrimary: "#1E5F8C",
	seriesPrimaryGlow: "rgba(30, 95, 140, 0.25)",
	plotBg: "#FFFFFF",
	plotBorder: "rgba(36, 26, 20, 0.2)",
	barTrack: "rgba(36, 26, 20, 0.08)",
	pointStroke: "#FFFFFF",
	// Structure triangle — same zone hierarchy, paper-readable labels
	structureBg: "#FFFFFF",
	structureOutline: "rgba(36, 26, 20, 0.55)",
	structureModerate: "#E8C9A0",
	structureModerateSoft: "#F3DFC4",
	structureReasonable: "#C5D9E8",
	structureGood: "#8FB8D9",
	structureOptimal: "#1E5F8C",
	structurePoor: "#E07A2F",
	structureSample: "#1A1E1C",
	structureSampleRing: "#FFFFFF",
	structureSampleHalo: "rgba(36, 26, 20, 0.12)",
	structureLabel: "#241A14",
	structureLabelMuted: "#5C4A38",
	structureTick: "#3A2418",
	structureArrow: "#C45C12",
	// Base sat cations — saturated on paper
	cationCa: "#3D7A42",
	cationMg: "#1E5F8C",
	cationK: "#C9A227",
	cationNa: "#B42318",
	cationOther: "rgba(90, 80, 70, 0.45)",
};

/** True while PDF snapshot capture is active. */
export function isPrintCaptureActive(): boolean {
	if (typeof document === "undefined") return false;
	return document.documentElement.dataset.printCapture === "true";
}

/** Active theme for chart components (UI vs print capture). */
export function getActiveChartTheme(): ChartTheme {
	return isPrintCaptureActive() ? printChartTheme : chartTheme;
}

/** Severity → color for salinity/sodicity gauges. */
export function severityColor(severity: string | null | undefined): string {
	switch (severity) {
		case "None":
			return chartTheme.severityNone;
		case "Slight":
			return chartTheme.severitySlight;
		case "Moderate":
			return chartTheme.severityModerate;
		case "Strong":
			return chartTheme.severityStrong;
		case "Severe":
			return chartTheme.severitySevere;
		default:
			return chartTheme.labelMuted;
	}
}

/** Coverage status → ring accent. */
export function coverageRingColor(
	status: "Met" | "Partial" | "Missing" | "NotRequired" | string
): string {
	if (status === "Met") return chartTheme.ringMet;
	if (status === "Missing") return chartTheme.ringMissing;
	if (status === "Partial") return chartTheme.ringPartial;
	return chartTheme.seriesPrimary;
}
